import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { loginState } from 'global-stores';
import {
	Kebab,
	PageHead,
	Pager,
	SearchBox,
	Tabs,
} from 'views/design-system';
import { CreateSignModal, CreateSignState, IsCreateSignModalOpen, useGenerateSignImage } from 'views/create-sign';
import { DocumentStartNow } from 'views/simplici-sign-dashboard/components/document-start-now';
import {
	SignDashboardSignatureState,
	SignDashboardState,
	useSignDashboard,
} from 'views/simplici-sign-dashboard/store';
import { TitleModal } from 'views/simplici-sign-dashboard/components/title-modal';
import {
	EditWebHookUrlModal,
	EditWebHookUrlModalForm,
} from 'views/simplici-sign-dashboard/components/esign-webhook-url/component';
import {
	EsignDratDocState,
	ISignedDocInbox,
	SignedDocInboxState,
	useSignedDocsInbox,
} from 'views/signed-doc-inbox';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';

import {
	CHIP,
	EDGE,
	TYPE_LABEL,
	actionOf,
	dayOf,
	explain,
	pageCount,
	timeOf,
} from './derive';
import { EnvelopeDrawer } from './envelope-drawer';
import { useEnvelopeSigning } from './use-envelope-signing';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './esign-deck.scss';

const PAGE_SIZE = 15;

type ITab = 'all' | 'pending' | 'underReview' | 'completed' | 'approved';

const TABS: { key: ITab; label: string }[] = [
	{ key: 'all', label: 'All' },
	{ key: 'pending', label: 'Pending' },
	{ key: 'underReview', label: 'Under Review' },
	{ key: 'completed', label: 'Completed' },
	{ key: 'approved', label: 'Reviewed' },
];

/**
 * The admin's own adopted signature. `getSignDashboard` fetches the signature
 * images itself when the account has one, so this card only reads state — no
 * second request.
 *
 * `.idck` (the verified checkmark) and `.idon` ("Active") are the design's way
 * of saying "this is set up" — they only render once a signature actually
 * exists, same as everywhere else in this rebuild that a real state is shown
 * rather than invented. `.idedit` is the one control this card cannot lose:
 * without it there was no way to adopt a FIRST signature at all, only to view
 * one that already existed.
 */
const SignatureCard = ({ canEdit }: { canEdit: boolean }) => {
	const { data: sig } = useRecoilValue(SignDashboardSignatureState);
	const setSignOpen = useSetRecoilState(IsCreateSignModalOpen);
	const hasSignature = !!sig?.signDetail?.url;

	return (
		<div className="idcard sig">
			{hasSignature && (
				<span className="idck" aria-hidden="true">
					<svg viewBox="0 0 12 12">
						<path
							d="M2.5 6.3 5 8.8 9.5 3.6"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.6"
						/>
					</svg>
				</span>
			)}
			{canEdit && (
				<button
					className="idedit"
					type="button"
					aria-label="Edit signature"
					onClick={() => setSignOpen(true)}
				>
					<svg viewBox="0 0 16 16" aria-hidden="true">
						<path
							d="M11.3 2.7l2 2L5.6 12.4l-2.8.8.8-2.8z"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.3"
						/>
						<path d="M9.8 4.2l2 2" fill="none" stroke="currentColor" strokeWidth="1.3" />
					</svg>
					Edit
				</button>
			)}
			<span className="idlbl">
				Signature
				{hasSignature && <span className="idon">Active</span>}
			</span>
			<div className="sigspecs">
				{hasSignature ? (
					<span className="spec sel">
						<span className="stab">Sign</span>
						<img src={sig.signDetail?.url} alt="Your signature" />
					</span>
				) : canEdit ? (
					<button
						className="spec"
						type="button"
						onClick={() => setSignOpen(true)}
					>
						<span className="idsub">No signature adopted yet — click Edit</span>
					</button>
				) : (
					<span className="spec">
						<span className="idsub">No signature adopted yet</span>
					</span>
				)}
				{sig?.initialDetail?.url && (
					<span className="spec">
						<span className="stab">Initials</span>
						<img src={sig.initialDetail.url} alt="Your initials" />
					</span>
				)}
			</div>
		</div>
	);
};

export const EsignDeck = () => {
	const { email } = useRecoilValue(loginState) ?? {};
	const { data: envelopes, isLoaded } = useRecoilValue(SignedDocInboxState);
	const { data: drafts } = useRecoilValue(EsignDratDocState);
	const { data: dash } = useRecoilValue(SignDashboardState);

	const { getSignedDocsInbox, resendSignDocInbox, downloadSignDocInbox, voidPacket } =
		useSignedDocsInbox();
	const { getSignDashboard } = useSignDashboard();
	const { handleCreateSignImages } = useGenerateSignImage();
	const userBioMetrics = useRecoilValue(CreateSignState);
	const [titleOpen, setTitleOpen] = useState(false);
	const [webhookOpen, setWebhookOpen] = useState(false);
	const { checkUserWritePermission } = useUserRoles();
	// Envelope actions (resend/void) and the page's own settings (signature,
	// title, webhook, starting a new envelope) are gated on different
	// permissions in production — Sent for the former, SignDashboard for the
	// latter — and conflating them would either over- or under-expose one.
	const canWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Sent),
		[checkUserWritePermission]
	);
	const canEditSettings = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.SignDashboard),
		[checkUserWritePermission]
	);
	const [busyRow, setBusyRow] = useState<string>('');

	const [tab, setTab] = useState<ITab>('all');
	const [typed, setTyped] = useState('');
	const [page, setPage] = useState(1);
	const [open, setOpen] = useState<ISignedDocInbox | null>(null);

	const reload = useCallback(() => {
		getSignedDocsInbox();
		getSignedDocsInbox(true, 'draft');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		reload();
		getSignDashboard(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Adopting a signature in the modal only sets the chosen name/style/initials
	// in CreateSignState — this is what turns that choice into the rendered
	// images the signature card and every future envelope actually use. Without
	// it mounted, the modal would appear to work and nothing would be saved.
	useEffect(() => {
		const { fullName, initials } = userBioMetrics ?? {};
		if (fullName && initials) handleCreateSignImages();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [handleCreateSignImages, userBioMetrics]);

	/* Every envelope, read once, with its true state attached. */
	const decorated = useMemo(
		() =>
			(envelopes ?? []).map(env => {
				const action = actionOf(env, email);
				return { env, action, chip: CHIP[action], why: explain(env, action) };
			}),
		[envelopes, email]
	);

	const counts = useMemo(() => {
		const by = (...a: string[]) =>
			decorated.filter(d => a.includes(d.action)).length;
		return {
			pending: by('waitingForOthers', 'needToSign'),
			underReview: by('underReview'),
			completed: by('completed'),
			approved: decorated.filter(d => (d.env.status as string) === 'approved').length,
			awaiting: by('needToSign'),
			waiting: by('waitingForOthers'),
			drafts: (drafts ?? []).length,
			completedToday: decorated.filter(
				d =>
					d.action === 'completed' &&
					new Date(d.env.updatedAt).toDateString() === new Date().toDateString()
			).length,
		};
	}, [decorated, drafts]);

	const filtered = useMemo(() => {
		const q = typed.trim().toLowerCase();
		return decorated.filter(({ env, action }) => {
			if (tab === 'pending' && !['waitingForOthers', 'needToSign'].includes(action))
				return false;
			if (tab === 'underReview' && action !== 'underReview') return false;
			if (tab === 'completed' && action !== 'completed') return false;
			if (tab === 'approved' && (env.status as string) !== 'approved') return false;
			if (!q) return true;
			const haystack = [
				env.name,
				...(env.recipients ?? []).map(r => `${r.fullName ?? r.name} ${r.email}`),
			]
				.join(' ')
				.toLowerCase();
			return haystack.includes(q);
		});
	}, [decorated, tab, typed]);

	const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	/* Signing — the row action, the kebab item, the drawer button and the widget
	   itself, shared with the Received deck. `signModal` is rendered below. */
	const { startSigning, signModal } = useEnvelopeSigning({
		email,
		reload,
		setBusyRow,
		onNoRecipient: setOpen,
	});

	/**
	 * The one action a row is actually for, by its actual state — resend the packet
	 * that is blocked, download the one that is finished, review the one waiting
	 * on compliance. Everything else stays in the kebab.
	 */
	const primaryAction = useCallback(
		(env: ISignedDocInbox, action: string) => {
			const busy = () => setBusyRow(env._id);
			const done = () => setBusyRow('');
			switch (action) {
				case 'needToSign':
					return { label: 'Sign', run: () => startSigning(env) };
				case 'completed':
					return {
						label: 'Download',
						run: () => {
							busy();
							downloadSignDocInbox(env._id, done);
						},
					};
				case 'waitingForOthers':
					return canWrite
						? {
								label: 'Resend',
								run: () => {
									busy();
									resendSignDocInbox(env._id, done);
								},
						  }
						: { label: 'Open', run: () => setOpen(env) };
				case 'underReview':
					return { label: 'Review', run: () => setOpen(env) };
				case 'voided':
					return { label: 'History', run: () => setOpen(env) };
				default:
					return { label: 'Open', run: () => setOpen(env) };
			}
		},
		[canWrite, downloadSignDocInbox, resendSignDocInbox, startSigning]
	);

	const kebabFor = useCallback(
		(env: ISignedDocInbox, action: string) => {
			const items: { label: string; onClick: () => void }[] = [
				{ label: 'Open details', onClick: () => setOpen(env) },
				{
					label: 'Download signed PDF',
					onClick: () => {
						setBusyRow(env._id);
						downloadSignDocInbox(env._id, () => setBusyRow(''));
					},
				},
			];
			// Only a packet actually waiting on this user can be signed, so the
			// item is absent rather than disabled everywhere else.
			if (action === 'needToSign') {
				items.unshift({ label: 'Sign', onClick: () => startSigning(env) });
			}
			if (canWrite) {
				items.push({
					label: 'Resend invitation',
					onClick: () => {
						setBusyRow(env._id);
						resendSignDocInbox(env._id, () => setBusyRow(''));
					},
				});
				if (env.status !== 'voided') {
					items.push({
						label: 'Void packet',
						onClick: async () => {
							// eslint-disable-next-line no-alert
							const reason = window.prompt(
								'Void this packet — reason for the audit trail:'
							);
							if (!reason) return;
							await voidPacket(env._id, { status: 'voided', statusMessage: reason });
							reload();
						},
					});
				}
			}
			return items;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[canWrite, startSigning]
	);

	const swapTab = useCallback((k: string) => {
		setTab(k as ITab);
		setPage(1);
	}, []);

	return (
		<div className="sv2-scope sv2 esign-deck">
			<PageHead
				title="eSign"
				sub={
					<>
						{(envelopes ?? []).length} envelopes &middot;{' '}
						{isLoaded ? 'synced' : 'syncing'} &middot; statuses read live from the
						envelope-list API
					</>
				}
			/>

			<div className="idstrip">
				<SignatureCard canEdit={canEditSettings} />
				<div className="idcard">
					{canEditSettings && (
						<button
							className="idedit"
							type="button"
							aria-label="Edit title"
							onClick={() => setTitleOpen(true)}
						>
							<svg viewBox="0 0 16 16" aria-hidden="true">
								<path
									d="M11.3 2.7l2 2L5.6 12.4l-2.8.8.8-2.8z"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.3"
								/>
								<path d="M9.8 4.2l2 2" fill="none" stroke="currentColor" strokeWidth="1.3" />
							</svg>
							Edit
						</button>
					)}
					<span className="idlbl">Title</span>
					<div className="idval">{dash?.title || '--'}</div>
					<div className="idsub">stamped into every title tab you sign</div>
				</div>
				<div className="idcard">
					{canEditSettings && (
						<button
							className="idedit"
							type="button"
							aria-label="Edit webhook"
							onClick={() => setWebhookOpen(true)}
						>
							<svg viewBox="0 0 16 16" aria-hidden="true">
								<path
									d="M11.3 2.7l2 2L5.6 12.4l-2.8.8.8-2.8z"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.3"
								/>
								<path d="M9.8 4.2l2 2" fill="none" stroke="currentColor" strokeWidth="1.3" />
							</svg>
							Edit
						</button>
					)}
					<span className="idlbl">Webhook</span>
					<div className="idval mono" title={dash?.webhookConfig?.url}>
						{dash?.webhookConfig?.url || 'not configured'}
					</div>
					<div className="idsub">envelope events POST here on completion</div>
				</div>
			</div>

			{canEditSettings && (
				<div className="start-now-shim">
					<DocumentStartNow />
				</div>
			)}

			<div className="strip estrip">
				<div className="tile">
					<div className="l">Awaiting your signature</div>
					<div className="n bl">{counts.awaiting}</div>
					<div className="d">need to sign · not yet signed</div>
				</div>
				<div className="tile">
					<div className="l">Waiting for others</div>
					<div className="n a">{counts.waiting}</div>
					<div className="d">blocked on a named recipient</div>
				</div>
				<div className="tile">
					<div className="l">Completed today</div>
					<div className="n g">{counts.completedToday}</div>
					<div className="d">printable copies delivered</div>
				</div>
				<div className="tile">
					<div className="l">Drafts</div>
					<div className="n gy">{counts.drafts}</div>
					<div className="d">configured · invitations not sent</div>
				</div>
			</div>

			<div className="ctl">
				<SearchBox
					value={typed}
					onChange={v => {
						setTyped(v);
						setPage(1);
					}}
					placeholder="Search subject or recipient"
					title="Searches the subject and every recipient's name and email"
				/>
				<Pager page={page} pages={pages} onChange={setPage} />
			</div>

			<Tabs
				tabs={TABS.map(t => ({
					...t,
					count: t.key === 'all' ? undefined : (counts as any)[t.key],
				}))}
				active={tab}
				onChange={swapTab}
			/>

			<div className="panel" style={{ marginTop: 12 }}>
				<div className="tscroll">
					<table className="stbl etbl">
						<thead>
							<tr>
								<th className="subcol">Subject</th>
								<th className="stcol">Status</th>
								<th>WorkFlow</th>
								<th>Type</th>
								<th>Face Auth.</th>
								<th>Created At</th>
								<th className="accol">Actions</th>
							</tr>
						</thead>
						<tbody>
							{shown.map(({ env, action, chip, why }) => (
								<tr
									key={env._id}
									className={`${EDGE[action]}${open?._id === env._id ? ' sel' : ''}`}
									onClick={() => setOpen(env)}
									title="Open envelope details"
								>
									<td className="od subcol">
										<span className="nm">{env.name || 'Untitled packet'}</span>
										<br />
										<span className="mail">
											{(env.recipients ?? [])
												.map(r => r.fullName || r.name || r.email)
												.join(' · ') || 'no recipients'}
										</span>
										<br />
										<span className="fund">
											{env.documents?.length ?? 0} document
											{(env.documents?.length ?? 0) === 1 ? '' : 's'} &middot;{' '}
											{pageCount(env)} pages
										</span>
									</td>

									<td className="fl stcol">
										<span className={`vch ${chip.cls}`}>{chip.label}</span>
										<div className={`wline${why.tone ? ` ${why.tone}` : ''}`}>
											{why.text}
										</div>
									</td>

									<td className="mid">{env.purpose ?? '--'}</td>
									<td className="mid">
										{TYPE_LABEL[env.purpose as string] ?? env.origin ?? '--'}
									</td>
									<td className="mid">
										<span className={`auth-chip${env.authType === 'facial' ? ' fac' : env.authType ? '' : ' none'}`}>
											{env.authType ?? '--'}
										</span>
									</td>
									<td className="mid">
										{dayOf(env.createdAt)}
										<span className="fund">{timeOf(env.createdAt)}</span>
									</td>
									<td className="accol">
										{(() => {
											const primary = primaryAction(env, action);
											return (
												<button
													className="reinv"
													type="button"
													disabled={busyRow === env._id}
													onClick={e => {
														e.stopPropagation();
														primary.run();
													}}
												>
													{busyRow === env._id ? '…' : primary.label}
												</button>
											);
										})()}
										<Kebab items={kebabFor(env, action)} />
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{!isLoaded && <div className="sv2-loading">Loading envelopes…</div>}
				{isLoaded && filtered.length === 0 && (
					<div className="sv2-empty">No envelopes match this filter.</div>
				)}
			</div>

			<div className="foot">
				A blocked envelope always names its blocking recipient and what it is waiting on
				&mdash; never a bare &ldquo;Waiting for others&rdquo;. The coloured left edge mirrors
				the envelope status. Click a row to open the envelope.
			</div>

			<EnvelopeDrawer
				envelope={open}
				open={!!open}
				onClose={() => setOpen(null)}
				onChanged={reload}
				onSign={env => {
					// The drawer is showing the envelope as it was before signing, so
					// it is dismissed rather than left behind the widget to go stale.
					setOpen(null);
					startSigning(env);
				}}
			/>

			{signModal}

			<CreateSignModal />

			<TitleModal isOpen={titleOpen} setModalClose={setTitleOpen} loading={false} />

			{webhookOpen && (
				<EditWebHookUrlModal name="" isOpen={webhookOpen} setIsOpen={setWebhookOpen}>
					<EditWebHookUrlModalForm setModalOpen={setWebhookOpen} />
				</EditWebHookUrlModal>
			)}
		</div>
	);
};

export default EsignDeck;
